import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { ShoppingCart, Menu, X, LogOut, ChevronDown } from 'lucide-react';
import { useState } from 'react';

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const links = [
    { to: '/', label: 'Home' },
    { to: '/all-courses', label: 'Courses', submenu: [
      { to: '/all-courses', label: 'All Courses' },
      { to: '/courses', label: 'Beginners' },
      { to: '/intermediate', label: 'Intermediate' },
      { to: '/advanced', label: 'Advanced' },
      { to: '/products', label: 'Robots' },
    ]},
    { to: '/products', label: 'Robots' },
    { to: '/affiliate', label: 'Affiliates' },
    { to: '/reviews', label: 'Reviews' },
    { to: '/contact', label: 'Contacts' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#050814]/80 backdrop-blur-xl border-b border-white/[0.06]" data-testid="main-header">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 md:px-12">
        {/* Logo */}
        <Link to="/" onClick={() => window.scrollTo(0, 0)} className="flex items-center gap-3" data-testid="logo-link">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-500 text-[#050814] font-black text-sm">1</div>
          <span className="text-lg font-semibold tracking-tight">
            <span className="text-amber-500 italic font-bold">1 on 1 Trade</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {links.map(l => (
            l.submenu ? (
              <div key={l.label} className="relative group" data-testid={`nav-${l.label.toLowerCase()}`}>
                <Link
                  to={l.to}
                  className={`flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${isActive(l.to) ? 'text-amber-400' : 'text-slate-400 hover:text-white'}`}
                >
                  {l.label}
                  <ChevronDown size={14} className="text-slate-500 group-hover:text-white/60 transition-colors" />
                </Link>
                <div className="absolute top-full left-0 pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="w-48 rounded-xl bg-[#0a0f1c] border border-white/10 py-2 shadow-xl">
                    {l.submenu.map(sub => (
                      <Link
                        key={sub.to + sub.label}
                        to={sub.to}
                        className="block px-4 py-2.5 text-sm text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                        data-testid={`subnav-${sub.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={l.to}
                to={l.to}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${isActive(l.to) ? 'text-amber-400' : 'text-slate-400 hover:text-white'}`}
                data-testid={`nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            )
          ))}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-4">
          <Link to="/cart" className="relative p-2 text-slate-400 hover:text-white transition-colors" data-testid="cart-link">
            <ShoppingCart size={20} />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-[#050814]" data-testid="cart-count">
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors"
                data-testid="user-menu-btn"
              >
                <span className="hidden sm:inline max-w-[100px] truncate">{user.name}</span>
                <ChevronDown size={14} />
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 rounded-xl bg-[#0a0f1c] border border-white/10 py-1 shadow-xl" data-testid="user-dropdown">
                  <div className="px-3 py-2 border-b border-white/10">
                    <p className="text-xs text-slate-500">Signed in as</p>
                    <p className="text-sm text-white truncate">{user.email}</p>
                  </div>
                  <Link
                    to="/my-courses"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-white/5"
                    data-testid="my-courses-link"
                  >
                    📚 My Courses
                  </Link>
                  {user.role === 'admin' && (
                    <Link
                      to="/admin/courses"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-amber-400 hover:text-amber-300 hover:bg-white/5"
                      data-testid="admin-courses-link"
                    >
                      ⚙️ Admin: Courses
                    </Link>
                  )}
                  <button
                    onClick={() => { logout(); setUserMenuOpen(false); }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-white/5"
                    data-testid="logout-btn"
                  >
                    <LogOut size={14} /> Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Link to="/login" className="text-sm text-slate-400 hover:text-white transition-colors hidden sm:inline" data-testid="login-link">
                Login
              </Link>
              <Link to="/register" className="px-5 py-2 rounded-full text-sm font-bold border border-amber-500 text-amber-500 hover:bg-amber-500/10 transition-colors" data-testid="get-started-link">
                Get Started
              </Link>
            </div>
          )}

          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2 text-slate-400 hover:text-white" data-testid="mobile-menu-toggle">
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-white/[0.06] bg-[#050814]/95 backdrop-blur-xl" data-testid="mobile-nav">
          <nav className="flex flex-col px-6 py-3 gap-1">
            {links.map(l => (
              <div key={l.label}>
                <Link
                  to={l.to}
                  onClick={() => !l.submenu && setMobileOpen(false)}
                  className={`block px-3 py-2.5 rounded-md text-sm ${isActive(l.to) ? 'text-amber-400' : 'text-slate-400'}`}
                >
                  {l.label}
                </Link>
                {l.submenu && (
                  <div className="pl-6">
                    {l.submenu.map(sub => (
                      <Link
                        key={sub.to + sub.label}
                        to={sub.to}
                        onClick={() => setMobileOpen(false)}
                        className="block px-3 py-2 text-sm text-slate-500 hover:text-white"
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
